/**
 * RFM Analysis Page
 *
 * Main page for RFM (Recency, Frequency, Monetary) customer segmentation analysis.
 * Displays all 11 customer segments with aggregated metrics and actionable insights.
 *
 * Features:
 * - Overview of all 11 RFM segments
 * - Interactive segment cards with click-through to details
 * - Period selection for historical analysis
 * - Export functionality
 * - Distribution matrix visualization
 * - Admin/SuperAdmin access only
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import { Users, TrendingUp, DollarSign } from "lucide-react";
import { RFMSegmentCard } from "@/frontend/components/rfm/RFMSegmentCard";
import { RFMMatrix, RFMMatrixMini } from "@/frontend/components/rfm/RFMMatrix";
import { RFMSegmentMap } from "@/frontend/components/rfm/RFMSegmentMap";
import { SegmentCustomerList } from "@/frontend/components/rfm/SegmentCustomerList";
import { RFMExportButton } from "@/frontend/components/rfm/RFMExportButton";
import { PeriodSelector } from "@/frontend/components/sales/PeriodSelector";
import {
  useRFMAnalysis,
  useRFMSegmentCustomers,
  useCurrentPeriod,
  useFormatPeriod
} from "@/frontend/hooks/useRFMData";
import type { RFMSegment } from "@/shared/types/rfm-analytics";
import { PAGE_TRANSLATIONS, STATS_TRANSLATIONS } from "@/shared/lib/rfm-translations";

export function RFMAnalysis() {
  const [searchParams] = useSearchParams();
  const currentPeriod = useCurrentPeriod();
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);
  const [selectedSegment, setSelectedSegment] = useState<RFMSegment | null>(null);
  const [segmentPage, setSegmentPage] = useState(1);

  // Get preview mode from URL (admin feature)
  const previewAs = searchParams.get("previewAs") || undefined;

  useFormatPeriod(selectedPeriod);

  // Fetch main RFM analysis
  const {
    data: rfmData,
    isLoading: isLoadingAnalysis,
    error: analysisError
  } = useRFMAnalysis({
    period: selectedPeriod,
    limit: 100, // Fetch 100 customers per segment for better matrix visualization
    previewAs
  });

  // Fetch segment details when a segment is selected
  const {
    data: segmentData,
    isLoading: isLoadingSegment
  } = useRFMSegmentCustomers(
    selectedSegment || undefined,
    {
      period: selectedPeriod,
      offset: (segmentPage - 1) * 50,
      limit: 50,
      previewAs
    },
    {
      enabled: !!selectedSegment
    }
  );

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate overview stats
  const overviewStats = rfmData ? {
    totalCustomers: rfmData.segments.reduce((sum, s) => sum + s.customerCount, 0),
    totalRevenue: rfmData.segments.reduce((sum, s) => sum + s.totalRevenue, 0),
    activeSegments: rfmData.segments.filter(s => s.customerCount > 0).length,
    topSegment: rfmData.segments.reduce((max, s) =>
      s.customerCount > max.customerCount ? s : max
    , rfmData.segments[0])
  } : null;

  // Handle segment card click
  const handleSegmentClick = (segmentName: RFMSegment) => {
    setSelectedSegment(segmentName);
    setSegmentPage(1);
    // Scroll to segment details section
    setTimeout(() => {
      document.getElementById('segment-details')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Handle closing segment details
  const handleCloseSegmentDetails = () => {
    setSelectedSegment(null);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{PAGE_TRANSLATIONS.title}</h1>
          <p className="text-gray-600 mt-1">
            {PAGE_TRANSLATIONS.subtitle}
          </p>
        </div>
        <RFMExportButton
          type="analysis"
          period={selectedPeriod}
          label={PAGE_TRANSLATIONS.exportAnalysis}
          variant="default"
        />
      </div>

      {/* Period Selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-medium">{PAGE_TRANSLATIONS.analysisPeriod}</span>
              <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
              <span className="text-sm text-gray-500">{PAGE_TRANSLATIONS.rollingWindow}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoadingAnalysis && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto" />
            <p className="mt-4 text-gray-600">{PAGE_TRANSLATIONS.loadingAnalysis}</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {analysisError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p className="font-medium">{PAGE_TRANSLATIONS.loadingFailed}</p>
              <p className="text-sm mt-1">{(analysisError as Error).message}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overview Stats */}
      {rfmData && overviewStats && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{STATS_TRANSLATIONS.totalCustomers}</CardTitle>
                <Users className="h-4 w-4 text-gray-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overviewStats.totalCustomers.toLocaleString()}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {overviewStats.activeSegments} {STATS_TRANSLATIONS.acrossSegments}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{STATS_TRANSLATIONS.totalRevenue}</CardTitle>
                <DollarSign className="h-4 w-4 text-gray-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(overviewStats.totalRevenue)}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {STATS_TRANSLATIONS.analysisPeriod}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{STATS_TRANSLATIONS.topSegment}</CardTitle>
                <TrendingUp className="h-4 w-4 text-gray-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overviewStats.topSegment.segment}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {overviewStats.topSegment.customerCount.toLocaleString()} {STATS_TRANSLATIONS.customers}
                  ({overviewStats.topSegment.percentage.toFixed(1)}%)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{STATS_TRANSLATIONS.distribution}</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-center">
                {(() => {
                  // Collect all customers from segments for mini matrix
                  const allCustomers = rfmData.segments.flatMap(segment => segment.customers);
                  return allCustomers.length > 0 && (
                    <RFMMatrixMini customers={allCustomers} />
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Segment Cards Grid */}
          <div>
            <h2 className="text-2xl font-bold mb-4">{PAGE_TRANSLATIONS.customerSegments}</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {rfmData.segments.map((segment) => (
                <RFMSegmentCard
                  key={segment.segment}
                  segment={segment}
                  onClick={() => handleSegmentClick(segment.segment as RFMSegment)}
                />
              ))}
            </div>
          </div>

          {/* RFM Segment Map */}
          {/* Single Source of Truth: Uses segment counts from backend API, not local counting */}
          <RFMSegmentMap
            segments={rfmData.segments}
            onSegmentClick={handleSegmentClick}
          />

          {/* Distribution Matrix */}
          {(() => {
            // Collect all customers from all segments for proper distribution visualization
            const allCustomers = rfmData.segments.flatMap(segment => segment.customers);

            return allCustomers.length > 0 && (
              <RFMMatrix
                customers={allCustomers}
                xAxis="frequency"
                yAxis="recency"
              />
            );
          })()}
        </>
      )}

      {/* Segment Details (shown when segment is selected) */}
      {selectedSegment && (
        <div id="segment-details" className="space-y-4 pt-6 border-t-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{selectedSegment} - {PAGE_TRANSLATIONS.segmentDetails}</h2>
              <p className="text-gray-600 mt-1">
                {PAGE_TRANSLATIONS.viewCustomers}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <RFMExportButton
                type="segment"
                segmentName={selectedSegment}
                period={selectedPeriod}
                size="sm"
              />
              <Button variant="outline" size="sm" onClick={handleCloseSegmentDetails}>
                {PAGE_TRANSLATIONS.close}
              </Button>
            </div>
          </div>

          {segmentData && (
            <SegmentCustomerList
              customers={segmentData.customers}
              totalCustomers={segmentData.pagination.total}
              currentPage={segmentPage}
              pageSize={segmentData.pagination.limit}
              hasMore={segmentData.pagination.hasMore}
              onPageChange={setSegmentPage}
              isLoading={isLoadingSegment}
            />
          )}
        </div>
      )}
    </div>
  );
}
